'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import type { RefObject } from 'react';
import type { useChatVirtualizer } from './useChatVirtualizer';
import {
  FOLLOWING_BOTTOM_STATE,
  followStateAfterScroll,
  shouldFollowBottom,
  type ChatScrollFollowState,
} from './chat-scroll-follow';
import {
  scrollTopForAnchor,
  type ChatScrollRow,
} from './chat-scroll-anchor';
import { useChatScrollSession } from './useChatScrollSession';

type ChatVirtualizer = Pick<
  ReturnType<typeof useChatVirtualizer>['virtualizer'],
  'scrollToIndex'
> & {
  measurementsCache?: readonly ChatScrollRow[];
};

type UseVirtualizedChatScrollParams = {
  renderItemsLength: number;
  firstRenderItemKey: string | null;
  stableIsLoading: boolean;
  hasPendingHITL?: boolean;
  totalSize: number;
  virtualizer: ChatVirtualizer;
  scrollElementRef: RefObject<HTMLDivElement | null>;
  onLoadMore?: () => Promise<void>;
  hasMoreMessages: boolean;
  isLoadingMoreMessages: boolean;
  pendingHitlIndex?: number | null;
  pendingHitlKey?: string | null;
  scrollSessionId?: string;
  isScrollerActive?: boolean;
};

export type VirtualizedChatScrollHandle = {
  scrollToEnd: () => void;
  isAtEnd: (threshold?: number) => boolean;
};

export function useVirtualizedChatScroll({
  renderItemsLength,
  firstRenderItemKey,
  stableIsLoading,
  hasPendingHITL = false,
  totalSize,
  virtualizer,
  scrollElementRef,
  onLoadMore,
  hasMoreMessages,
  isLoadingMoreMessages,
  pendingHitlIndex = null,
  pendingHitlKey = null,
  scrollSessionId,
  isScrollerActive = true,
}: UseVirtualizedChatScrollParams): VirtualizedChatScrollHandle {
  const loadMoreRequestedRef = useRef(false);
  const previousHasPendingHITLRef = useRef(hasPendingHITL);
  const previousItemCountRef = useRef(0);
  const previousFirstRenderItemKeyRef = useRef<string | null>(
    firstRenderItemKey,
  );
  const followStateRef = useRef<ChatScrollFollowState>({
    isUserNearBottom: true,
    shouldKeepFollowingBottom: false,
  });
  const isInitialScrollPendingRef = useRef(false);
  const lastUnderfilledRequestItemCountRef = useRef<number | null>(null);
  const previousFollowItemCountRef = useRef(renderItemsLength);
  const previousFollowTotalSizeRef = useRef(totalSize);
  const previousStableIsLoadingRef = useRef(stableIsLoading);
  const lastScrollTopRef = useRef(0);
  const lastScrollHeightRef = useRef(0);
  const stableIsLoadingRef = useRef(stableIsLoading);
  const ignoreAnchorScrollRef = useRef(false);
  const focusedHitlKeyRef = useRef<string | null>(null);

  stableIsLoadingRef.current = stableIsLoading;

  const rows = virtualizer.measurementsCache ?? [];

  const isScrollNearBottom = useCallback(() => {
    const scrollElement = scrollElementRef.current;

    if (!scrollElement) {
      return true;
    }

    return (
      scrollElement.scrollHeight -
        scrollElement.scrollTop -
        scrollElement.clientHeight <
      150
    );
  }, [scrollElementRef]);

  const scrollToLastItem = useCallback(() => {
    if (renderItemsLength === 0) {
      return;
    }

    const scrollElement = scrollElementRef.current;

    if (scrollElement) {
      scrollElement.scrollTop = scrollElement.scrollHeight;

      return;
    }

    virtualizer.scrollToIndex(renderItemsLength - 1, { align: 'end' });
  }, [renderItemsLength, scrollElementRef, virtualizer]);

  const scrollToEnd = useCallback(() => {
    followStateRef.current = FOLLOWING_BOTTOM_STATE;
    scrollToLastItem();
  }, [scrollToLastItem]);

  const { anchorRef, rememberPosition, restoreSavedPosition } =
    useChatScrollSession({
      rows,
      scrollElementRef,
      isScrollNearBottom,
      followStateRef,
      isInitialScrollPendingRef,
      scrollSessionId,
      isScrollerActive,
      scrollToLastItem,
    });

  const isAtEnd = useCallback(
    (threshold?: number) => {
      const scrollElement = scrollElementRef.current;

      if (!scrollElement) {
        return true;
      }

      return (
        scrollElement.scrollHeight -
          scrollElement.scrollTop -
          scrollElement.clientHeight <=
        (threshold ?? 100)
      );
    },
    [scrollElementRef],
  );

  const requestLoadMore = useCallback(async () => {
    if (isLoadingMoreMessages || loadMoreRequestedRef.current || !onLoadMore) {
      return;
    }

    loadMoreRequestedRef.current = true;

    try {
      await onLoadMore();
    } catch (error: unknown) {
      console.warn('[VirtualizedChatMessageList] loadMore failed', error);
    } finally {
      loadMoreRequestedRef.current = false;
    }
  }, [isLoadingMoreMessages, onLoadMore]);

  useEffect(() => {
    const scrollElement = scrollElementRef.current;

    if (!scrollElement) {
      return;
    }

    lastScrollTopRef.current = scrollElement.scrollTop;
    lastScrollHeightRef.current = scrollElement.scrollHeight;

    const handleScroll = () => {
      const previousScrollTop = lastScrollTopRef.current;
      const previousScrollHeight = lastScrollHeightRef.current;
      const nextScrollTop = scrollElement.scrollTop;
      const nextScrollHeight = scrollElement.scrollHeight;
      const heightGrew = nextScrollHeight > previousScrollHeight;
      const userScrolledUp = nextScrollTop + 1 < previousScrollTop;

      if (ignoreAnchorScrollRef.current) {
        ignoreAnchorScrollRef.current = false;
      }

      lastScrollTopRef.current = nextScrollTop;
      lastScrollHeightRef.current = nextScrollHeight;

      followStateRef.current = followStateAfterScroll({
        state: followStateRef.current,
        stableIsLoading: stableIsLoadingRef.current,
        heightGrew,
        userScrolledUp,
        isNearBottom: isScrollNearBottom(),
        isInitialScrollPending: isInitialScrollPendingRef.current,
      });
      rememberPosition(nextScrollTop);
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [isScrollNearBottom, rememberPosition, scrollElementRef]);

  useLayoutEffect(() => {
    const previousItemCount = previousItemCountRef.current;
    const previousFirstRenderItemKey = previousFirstRenderItemKeyRef.current;
    const currentItemCount = renderItemsLength;

    previousItemCountRef.current = currentItemCount;
    previousFirstRenderItemKeyRef.current = firstRenderItemKey;

    const wasInitialLoad = previousItemCount === 0 && currentItemCount > 0;

    if (wasInitialLoad) {
      const scrollElement = scrollElementRef.current;

      if (scrollElement && restoreSavedPosition(scrollElement)) {
        return;
      }

      isInitialScrollPendingRef.current = true;
      followStateRef.current = {
        ...followStateRef.current,
        shouldKeepFollowingBottom: true,
      };
      scrollToLastItem();

      return;
    }

    const wasPrepend =
      previousItemCount > 0 &&
      currentItemCount > previousItemCount &&
      previousFirstRenderItemKey !== null &&
      firstRenderItemKey !== previousFirstRenderItemKey &&
      !followStateRef.current.isUserNearBottom;

    if (!wasPrepend) {
      return;
    }

    const scrollElement = scrollElementRef.current;

    if (!scrollElement) {
      return;
    }

    const anchor = anchorRef.current;

    if (!anchor) {
      return;
    }

    const nextTop = scrollTopForAnchor({
      anchor,
      rows,
    });

    if (nextTop === null) {
      return;
    }

    scrollElement.scrollTop = nextTop;
  }, [
    firstRenderItemKey,
    renderItemsLength,
    restoreSavedPosition,
    rows,
    scrollElementRef,
    scrollToLastItem,
    totalSize,
  ]);

  useEffect(() => {
    const resolvedLastPendingHITL =
      previousHasPendingHITLRef.current && !hasPendingHITL;

    previousHasPendingHITLRef.current = hasPendingHITL;

    if (resolvedLastPendingHITL) {
      focusedHitlKeyRef.current = null;
      scrollToEnd();
    }
  }, [hasPendingHITL, scrollToEnd]);

  useEffect(() => {
    const itemsChanged =
      renderItemsLength !== previousFollowItemCountRef.current;
    const totalSizeChanged = totalSize !== previousFollowTotalSizeRef.current;
    const loadingJustEnded =
      previousStableIsLoadingRef.current && !stableIsLoading;

    previousFollowItemCountRef.current = renderItemsLength;
    previousFollowTotalSizeRef.current = totalSize;
    previousStableIsLoadingRef.current = stableIsLoading;

    if (
      pendingHitlIndex !== null &&
      pendingHitlKey !== null &&
      focusedHitlKeyRef.current !== pendingHitlKey
    ) {
      focusedHitlKeyRef.current = pendingHitlKey;
      followStateRef.current = FOLLOWING_BOTTOM_STATE;
      ignoreAnchorScrollRef.current = true;
      virtualizer.scrollToIndex(pendingHitlIndex, { align: 'center' });

      return;
    }

    if (pendingHitlKey === null) {
      focusedHitlKeyRef.current = null;
    }

    const shouldFollow = shouldFollowBottom({
      state: followStateRef.current,
      stableIsLoading,
      loadingJustEnded,
      contentChanged: itemsChanged || totalSizeChanged,
    });

    if (!shouldFollow) {
      return;
    }

    scrollToLastItem();
    if (isScrollNearBottom()) {
      isInitialScrollPendingRef.current = false;
    }
  }, [
    isScrollNearBottom,
    pendingHitlIndex,
    pendingHitlKey,
    renderItemsLength,
    scrollToLastItem,
    stableIsLoading,
    totalSize,
    virtualizer,
  ]);

  useEffect(() => {
    const scrollElement = scrollElementRef.current;

    if (!scrollElement || !onLoadMore || !hasMoreMessages) {
      return;
    }

    const handleScroll = async () => {
      if (scrollElement.scrollTop > 120) {
        return;
      }

      await requestLoadMore();
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [hasMoreMessages, onLoadMore, requestLoadMore, scrollElementRef]);

  useEffect(() => {
    const scrollElement = scrollElementRef.current;

    if (!scrollElement || !onLoadMore || !hasMoreMessages) {
      return;
    }

    if (
      renderItemsLength === 0 ||
      lastUnderfilledRequestItemCountRef.current === renderItemsLength
    ) {
      return;
    }

    if (scrollElement.scrollHeight > scrollElement.clientHeight) {
      return;
    }

    lastUnderfilledRequestItemCountRef.current = renderItemsLength;
    void requestLoadMore();
  }, [
    hasMoreMessages,
    onLoadMore,
    renderItemsLength,
    requestLoadMore,
    scrollElementRef,
    totalSize,
  ]);

  return useMemo(
    () => ({
      scrollToEnd,
      isAtEnd,
    }),
    [isAtEnd, scrollToEnd],
  );
}
